import {
    ClickAwayListener,
    Hidden,
    IconButton,
    Input,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Popover,
    Popper,
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {Clear as ClearIcon, Search as SearchIcon} from '@material-ui/icons';
import moment from 'moment';
import {useRouter} from 'next/router';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {setTextAndBackgroundColor} from '../..';
import {ListingIcon, ReservationsIcon, UserIcon} from '../../../../../../components/Icons';
import Tabs from '../../../../../../components/Tabs';
import {debounce} from '../../../../../../libs/throttle-debounce';
import dateFormat from '../../../../../../libs/localizeDate';

const useStyles = (user = {}) =>
    makeStyles((theme) => {
        let {textColor, backgroundColor} = setTextAndBackgroundColor(theme, user);

        return {
            flexGrow: {
                flexGrow: 1,
            },
            search: {
                backgroundColor: 'rgba(255,255,255, 0.1)',
                borderRadius: 4,
                flexBasis: 300,
                height: 36,
                padding: theme.spacing(0, 2),
                display: 'flex',
                alignItems: 'center',
            },
            searchIcon: {
                marginRight: theme.spacing(2),
                color: 'inherit',
            },
            searchInput: {
                flexGrow: 1,
                color: 'inherit',
                '& input::placeholder': {
                    opacity: 1,
                    color: 'textSecondary',
                },
            },
            searchPopper: {
                zIndex: theme.zIndex.appBar + 100,
            },
            searchPopperContent: {
                marginTop: theme.spacing(1),
            },
            searchContentScroll: {
                maxHeight: 300,
                overflowY: 'auto',
            },
            clearIcon: {
                padding: 10,
            },
            smallScreenSearch: {
                padding: '16px',
                backgroundColor,
                color: textColor,
            },
        };
    });

export default function SearchAppBar({searchResults, onSearch, onClear, userType, selectUser, user}) {
    const classes = useStyles(user)();
    const router = useRouter();
    const searchRef = React.useRef(null);
    const smallScreenSearchButtonRef = React.useRef(null);
    const [openSearchPopover, setOpenSearchPopover] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState('');
    const [tab, setTab] = React.useState(userType === 'admin' ? 'users' : 'reservations');
    const [drawerOpen, setDrawerOpen] = useState(false);

    const handleSearchChange = (event) => {
        setSearchValue(event.target.value);
        if (event.target.value) {
            if (!openSearchPopover) {
                setOpenSearchPopover(true);
            }
            delay(event.target.value);
        } else {
            setOpenSearchPopover(false);
        }
    };

    // debounce search calls
    const delay = React.useRef(debounce((value) => onSearch(value), 300)).current;

    const handleSearchPopverClose = () => {
        setOpenSearchPopover(false);
        setSearchValue('');
        onClear();
    };

    const handleSearchSelect = (resource, id) => () => {
        handleSearchPopverClose();
        if (['reservation', 'listing'].includes(resource)) {
            router.push(`/${resource}/${id}`);
        }
        if (resource === 'user') {
            selectUser(id);
        }
    };

    const {listings, reservations, users} = searchResults;
    const emptyListings = !listings || listings.length === 0;
    const tabsList = [
        {label: 'Reservations', value: 'reservations'},
        {label: 'Listings', value: 'listings', disabled: emptyListings},
    ];
    if (userType === 'channel') {
        tabsList.push({label: 'Users', value: 'users'});
    }
    if (userType === 'admin') {
        tabsList.splice(0, 0, {label: 'Users', value: 'users'});
    }

    function renderSearchBar({isMobile}) {
        return (
            <>
                <div ref={searchRef} className={classes.search}>
                    <SearchIcon className={classes.searchIcon} />
                    <Input
                        className={classes.searchInput}
                        disableUnderline
                        onChange={handleSearchChange}
                        placeholder="Search anything"
                        value={searchValue}
                        autoFocus={isMobile}
                    />
                    {searchValue && searchValue.length > 0 && (
                        <IconButton className={classes.iconButton} aria-label="clear" onClick={handleSearchPopverClose}>
                            <ClearIcon />
                        </IconButton>
                    )}
                </div>
                <Popper
                    anchorEl={searchRef.current}
                    className={classes.searchPopper}
                    open={openSearchPopover}
                    placement="bottom"
                    modifiers={{offset: '271px 0px'}} // offset for sidebar shift
                >
                    <ClickAwayListener onClickAway={handleSearchPopverClose}>
                        <Paper className={classes.searchPopperContent} elevation={3}>
                            <Tabs
                                onClick={(event, tab) => setTab(tab)}
                                tab={tab}
                                tabs={tabsList}
                                tabsProps={{
                                    centered: true,
                                    variant: 'fullWidth',
                                }}
                            />

                            <div className={classes.searchContentScroll}>
                                {tab === 'reservations' && reservations && reservations.length > 0 && (
                                    <List key="reservations">
                                        {reservations.map((r) => {
                                            const checkInDate = dateFormat({
                                                dateString: r.check_in_date,
                                                timezone: r.listing_timezone_name || 'UTC',
                                                name: 'checkin',
                                                displayFormat: 'MMM D',
                                            }) || 'Date unavailable';
                                            const checkOutDate = dateFormat({
                                                dateString: r.check_out_date,
                                                timezone: r.listing_timezone_name || 'UTC',
                                                name: 'checkout',
                                                displayFormat: 'MMM D YYYY',
                                            }) || 'Date unavailable';
                                            return (
                                                <ListItem
                                                    button
                                                    key={r.id}
                                                    onClick={handleSearchSelect('reservation', r.id)}
                                                >
                                                    <ListItemIcon>
                                                        <ReservationsIcon />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={`${r.guest.first_name} (${r.number_of_guests} ${
                                                            r.number_of_guests > 1 ? 'guests' : 'guest'
                                                        }) - ${r.confirmation_code}`}
                                                        secondary={`${checkInDate} - ${checkOutDate} @ ${r.listing_nickname}`}
                                                    />
                                                </ListItem>
                                            )
                                        })}
                                    </List>
                                )}

                                {tab === 'listings' && listings && listings.length > 0 && (
                                    <List key="listings">
                                        {listings.map((r) => (
                                            <ListItem button key={r.id} onClick={handleSearchSelect('listing', r.id)}>
                                                <ListItemIcon>
                                                    <ListingIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={r.nickname}
                                                    secondary={`${r.location.city}, ${r.location.country}`}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                )}

                                {tab === 'users' && users && users.length > 0 && (
                                    <List key="users">
                                        {users.map((r) => (
                                            <ListItem button key={r.id} onClick={handleSearchSelect('user', r.id)}>
                                                <ListItemIcon>
                                                    <UserIcon />
                                                </ListItemIcon>
                                                <ListItemText primary={r.name} secondary={`${r.email}`} />
                                            </ListItem>
                                        ))}
                                    </List>
                                )}
                            </div>
                        </Paper>
                    </ClickAwayListener>
                </Popper>
            </>
        );
    }

    return (
        <>
            <Hidden smUp>
                <IconButton onClick={() => setDrawerOpen((open) => !open)} style={{color: 'inherit'}}>
                    <SearchIcon className={classes.searchIcon} ref={smallScreenSearchButtonRef} />
                </IconButton>
                <Popover
                    anchorEl={smallScreenSearchButtonRef.current}
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                    className={classes.searchPopper}
                >
                    <div className={classes.smallScreenSearch}>{renderSearchBar({isMobile: true})}</div>
                </Popover>
            </Hidden>
            <Hidden xsDown>{renderSearchBar({isMobile: false})}</Hidden>
        </>
    );
}

SearchAppBar.propTypes = {
    searchResults: PropTypes.object.isRequired,
    onSearch: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
    userType: PropTypes.string.isRequired,
    user: PropTypes.object.isRequired,
};
